/**
 * Performance measurement adapter: uses react-native-lighthouse with Indeed signals logging.
 * Keeps the same API (provider, grps) for drop-in compatibility.
 */
import { getDeviceProperties } from '../../logging/LoggableDeviceProperties';

import {
    log as logRNViewJobPerformance,
    AvroOptional,
} from '@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNViewJobPerformance';
import { useRef } from 'react';
import {
    usePerformanceMeasurement as usePerformanceMeasurementOSS,
    type PerformanceMetrics,
    type FirstInputDelayMetrics,
    type PerformanceHookResult,
} from 'react-native-lighthouse';

// Re-export types from OSS (metrics and result are compatible)
export type { PerformanceMetrics, FirstInputDelayMetrics, PerformanceHookResult };

/**
 * Options for usePerformanceMeasurement (Indeed-specific: provider, grps)
 */
export interface WithPerformanceMeasurementOptions {
    /**
     * Provider name for logging (e.g., 'viewjob', 'homepage')
     */
    provider: string;

    /**
     * Component name for identification in logs
     */
    componentName: string;

    /**
     * Timeout in ms to wait for FID before logging metrics anyway
     * @default 5000
     */
    fidTimeout?: number;

    /**
     * Custom callback when performance metrics are available
     */
    onMetricsReady?: (metrics: PerformanceMetrics) => void;

    /**
     * Custom callback when component becomes interactive
     */
    onInteractive?: (metrics: PerformanceMetrics) => void;

    /**
     * Proctor groups for logging
     */
    grps?: string | null;
}

/**
 * Log performance metrics using the existing RNViewJobPerformance schema
 * and enhanced console logging for debugging
 */
function logPerformanceMetrics(
    provider: string,
    componentName: string,
    metrics: PerformanceMetrics,
    grps?: string | null,
): void {
    const deviceProperties = getDeviceProperties();

    logRNViewJobPerformance({
        deviceProperties,
        baseMetrics: {
            timeToInteractiveMs: metrics.timeToInteractiveMs
                ? AvroOptional.long(metrics.timeToInteractiveMs)
                : AvroOptional.long(null),
        },
        fetchStartMs: AvroOptional.long(null),
        fetchEndMs: AvroOptional.long(null),
        timeToFirstFrameMs: AvroOptional.long(metrics.timeToFirstFrameMs),
        mountStartMs: AvroOptional.long(metrics.mountStartTimeMs),
        firstInputDelayMs: metrics.firstInputDelay
            ? AvroOptional.long(metrics.firstInputDelay.firstInputDelayMs)
            : AvroOptional.long(null),
        firstInputType: metrics.firstInputDelay
            ? AvroOptional.string(metrics.firstInputDelay.inputType)
            : AvroOptional.string(null),
        provider: AvroOptional.string(provider),
        componentName: AvroOptional.string(componentName),
        grps: AvroOptional.string(grps),
    });

    console.log(`[Performance] ${provider}/${componentName}:`, {
        TTFF_ms: metrics.timeToFirstFrameMs,
        TTI_ms: metrics.timeToInteractiveMs,
        FID_ms: metrics.firstInputDelay?.firstInputDelayMs,
        FID_type: metrics.firstInputDelay?.inputType,
        mountStart: metrics.mountStartTimeMs,
        firstFrame: metrics.firstFrameTimeMs,
        interactive: metrics.interactiveTimeMs,
        firstInput: metrics.firstInputDelay?.firstInputTimeMs,
    });
}

/**
 * Hook for measuring component performance metrics (TTFF, TTI, FID).
 * Delegates to react-native-lighthouse and sends metrics to Indeed signals.
 */
export function usePerformanceMeasurement(
    options: WithPerformanceMeasurementOptions,
): PerformanceHookResult {
    const {
        provider,
        componentName,
        fidTimeout = 5000,
        onMetricsReady,
        onInteractive,
        grps,
    } = options;

    // `grps` (and provider/componentName) commonly arrive from async native
    // context that hydrates after first render. react-native-lighthouse caches
    // its FID timeout the moment `markInteractive` is called and freezes its
    // PanResponder on first render, so both report paths can fire against the
    // original `onReport` closure. Routing log inputs through a ref keeps the
    // logged values in sync with whatever the component last received.
    const latestRef = useRef({ provider, componentName, grps });
    latestRef.current = { provider, componentName, grps };

    return usePerformanceMeasurementOSS({
        componentName,
        namespace: provider,
        fidTimeout,
        onMetricsReady,
        onInteractive,
        onReport: (metrics: PerformanceMetrics) => {
            const {
                provider: latestProvider,
                componentName: latestComponentName,
                grps: latestGrps,
            } = latestRef.current;
            logPerformanceMetrics(
                latestProvider,
                latestComponentName,
                metrics,
                latestGrps,
            );
        },
    });
}
